import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, PackageCheck } from 'lucide-react';
import { Button, Dialog, Input, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { CellPicker } from './CellPicker.js';
import type { CellDto } from '../../warehouse/api/warehouse.api.js';
import { stockApi } from '../api/stock.api.js';
import type { Unit } from '@eombor/contracts';

/** Miqdorni ekranga chiqarish — `numeric(14,3)` bilan bir xil yumaloqlash. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/** Shartnomadagi bilan bir xil: uch belgidan qisqa sabab hech nima aytmaydi. */
const MIN_REASON_LENGTH = 3;

export interface MoveStockTarget {
  batchId: string;
  batchNo: string;
  nomenclatureId: string;
  nomenclatureName: string;
  unit: Unit;
  /** Katakdagi UMUMIY miqdor — bandi bilan birga. */
  qty: number;
  /** Band qilinmagan qism — faqat shuni ko'chirish mumkin. */
  availableQty: number;
}

export interface MoveStockDialogProps {
  open: boolean;
  onClose: () => void;
  warehouseId: string;
  /** Qaysi katakdan — kod ekranda ko'rinadi, `id` so'rovga ketadi. */
  fromCellId: string;
  fromCellCode: string;
  target: MoveStockTarget | null;
  /** Ko'chirish bajarildi — chaqiruvchi ro'yxatni yangilaydi. */
  onMoved: () => void;
}

/**
 * MAHSULOTNI BOSHQA KATAKKA KO'CHIRISH.
 *
 * Nima uchun kerak bo'lgani aniq bir holatdan kelib chiqadi: mahsulot
 * `01-02-03` ga tushishi kerak edi, skladchi esa uni `01-01-03` ga qo'yib
 * yuborgan. Ilgari buni tuzatishning yo'li yo'q edi — server ko'chirishni
 * bilardi, ekran esa uni taklif qilmasdi. Skladchi mahsulotni fizik
 * ko'chirar, tizimda esa u eski katakda qolib ketardi va navbatdagi odam
 * uni topolmasdi.
 *
 * SABAB MAJBURIY va bu oynaning asosiy qoidasi. Ko'chirish tarixdagi eng
 * chalkash harakat: mahsulot bir joydan yo'qolib, boshqasida paydo
 * bo'ladi. Inventarizatsiyada birinchi beriladigan savol — "kim va nega
 * ko'chirdi?"; sabab yozilmasa, javob qolmaydi.
 *
 * BAND QILINGAN qism ko'chirilmaydi: u talabnomaga biriktirilgan va
 * skladchi uni o'sha katakdan berishi kerak. Shuning uchun chegara —
 * `availableQty`, `qty` emas.
 */
export const MoveStockDialog = ({
  open,
  onClose,
  warehouseId,
  fromCellId,
  fromCellCode,
  target,
  onMoved,
}: MoveStockDialogProps) => {
  const [code, setCode] = useState('');
  const [toCell, setToCell] = useState<CellDto | null>(null);
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');

  /* Har ochilishda toza boshlanadi: oldingi mahsulotning kodi va sababi
     qolib ketsa, skladchi uni ko'rmasdan tasdiqlab yuborardi. */
  useEffect(() => {
    if (!open) return;
    setCode('');
    setToCell(null);
    setQty(target === null ? '' : qtyText(target.availableQty));
    setReason('');
    move.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutatsiya har chizishda yangi
  }, [open, target?.batchId]);

  const move = useMutation({
    mutationFn: () =>
      stockApi.move({
        batchId: target?.batchId ?? '',
        fromCellId,
        toCellId: toCell?.id ?? '',
        qty: Number(qty),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      onMoved();
      onClose();
    },
  });

  const amount = Number(qty);
  const limit = target?.availableQty ?? 0;
  const qtyValid = qty !== '' && Number.isFinite(amount) && amount > 0 && amount <= limit;
  /* O'z katagiga ko'chirish — ma'nosiz amal va server uni baribir rad
     etadi. Uni shu yerda to'xtatish xato xabarini kutishdan yaxshiroq. */
  const sameCell = toCell !== null && toCell.id === fromCellId;
  const canMove =
    target !== null &&
    toCell !== null &&
    !sameCell &&
    qtyValid &&
    reason.trim().length >= MIN_REASON_LENGTH &&
    !move.isPending;

  const unit = target === null ? '' : unitText(target.unit);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('stock.moveTitle')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('stock.moveCancel')}
          </Button>
          <Button onClick={() => move.mutate()} loading={move.isPending} disabled={!canMove}>
            {t('stock.moveConfirm')}
          </Button>
        </>
      }
    >
      {target === null ? null : (
        <div className="flex flex-col gap-4">
          {/* NIMA ko'chirilyapti va QAYERDAN — skladchi tasdiqlashdan
              oldin ikkovini ham ko'rib turishi kerak. */}
          <div className="border-border bg-surface-2 flex items-start gap-3 rounded-[var(--radius-sm)] border px-3 py-3">
            <span
              aria-hidden
              className="bg-accent-soft text-accent flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
            >
              <PackageCheck size={18} />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-text text-sm font-medium text-balance">
                {target.nomenclatureName}
              </span>
              <span className="tabular text-text-faint text-xs">
                {target.batchNo} · {t('stock.moveFrom')} {fromCellCode}
              </span>
            </div>
          </div>

          <CellPicker
            warehouseId={warehouseId}
            value={code}
            onChange={setCode}
            onResolved={setToCell}
            nomenclatureId={target.nomenclatureId}
            label={t('stock.moveToCell')}
          />

          {!sameCell ? null : (
            <p role="alert" className="text-danger text-sm">
              {t('stock.moveSameCell')}
            </p>
          )}

          {toCell === null ? null : (
            <p className="text-text-muted flex items-center gap-2 text-sm">
              <span className="tabular">{fromCellCode}</span>
              <ArrowRight size={14} aria-hidden className="text-text-faint shrink-0" />
              <span className="tabular text-text font-medium">{toCell.code}</span>
            </p>
          )}

          <Input
            label={t('stock.moveQty')}
            inputMode="decimal"
            value={qty}
            onChange={(event) => setQty(event.target.value)}
            hint={t('stock.moveQtyHint', { qty: qtyText(limit), unit })}
            {...(qty !== '' && !qtyValid ? { error: t('stock.moveQtyInvalid') } : {})}
          />

          {/* BAND QILINGANI alohida aytiladi: skladchi "katakda 40 ta bor,
              nega 25 tasini ko'chira olmayapman?" degan savolga javobni
              shu yerda topadi. */}
          {target.availableQty >= target.qty ? null : (
            <p className="text-text-faint text-xs">
              {t('stock.moveReserved', {
                qty: qtyText(target.qty - target.availableQty),
                unit,
              })}
            </p>
          )}

          <Input
            label={t('stock.moveReason')}
            placeholder={t('stock.moveReasonPlaceholder')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            hint={t('stock.moveReasonHint')}
          />

          {move.error === null || move.error === undefined ? null : (
            <p
              role="alert"
              className={cn(
                'border-danger/30 bg-danger-soft text-danger rounded-[var(--radius-sm)]',
                'border px-3 py-2 text-sm',
              )}
            >
              {move.error instanceof ApiClientError ? move.error.message : t('stock.moveFailed')}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
};
